/**
 * Catalogue de contenu en français pour la fonctionnalité `auth` (connexion, enregistrement,
 * vérification de lien magique, réinitialisation / définition de mot de passe). Les clés
 * correspondent 1:1 aux appels `t('auth.*')` dans `packages/@gamecodex/world-ui/src/features/auth/*`.
 * Les valeurs sont les chaînes originales traduites en français.
 */
export const authFr = {
  // ── AuthShell ────────────────────────────────────────────────────────────
  'auth.shell.homeAriaLabel': 'Accueil Grymwyrd',
  'auth.shell.logoAlt': 'Grymwyrd',

  // ── OAuthButtons ─────────────────────────────────────────────────────────
  'auth.oauth.continueWithGoogle': 'Continuer avec Google',
  'auth.oauth.continueWithDiscord': 'Continuer avec Discord',

  // ── AccountVerifyPage ────────────────────────────────────────────────────
  'auth.verify.seoTitle': 'Connexion en cours',
  'auth.verify.seoDescription': 'Vérification de ton lien de connexion à Grymwyrd.',
  'auth.verify.errorMissingToken':
    'Ce lien de connexion ne possède pas son jeton. Demande un nouveau lien sur la page de connexion.',
  'auth.verify.errorInvalidOrExpired':
    'Ce lien de connexion est invalide ou a expiré. Demande un nouveau lien sur la page de connexion.',
  'auth.verify.confirming': 'Confirmation de ta connexion…',
  'auth.verify.signedInHeading': 'Tu es connecté',
  'auth.verify.signedInAsPrefix': 'Connecté en tant que',
  'auth.verify.signedInAsSuffix': '. Redirection vers ton sanctuaire…',
  'auth.verify.takingYouToSanctum': 'Redirection vers ton sanctuaire…',
  'auth.verify.goNow': 'Aller maintenant',
  'auth.verify.problemHeading': 'Problème avec le lien de connexion',
  'auth.verify.backToSignIn': 'Retour à la connexion',

  // ── LoginPage ────────────────────────────────────────────────────────────
  'auth.login.errorEnterEmailFirst': 'Saisis ton adresse email ci-dessus, puis demande un lien.',
  'auth.login.seoTitle': 'Franchis le Seuil',
  'auth.login.seoDescription': 'Connecte-toi à Grymwyrd et retourne à ta légende dans le crépuscule éternel.',
  'auth.login.eyebrow': 'Reviens, Errant',
  'auth.login.title': 'Franchis le Seuil',
  'auth.login.blurb': 'Énonce ton nom et le mot secret pour réentrer dans le crépuscule éternel.',
  'auth.login.noPactYet': 'Pas encore de pacte ?',
  'auth.login.forgeYourLegend': 'Forge ta légende',
  'auth.login.emailLabel': 'Email',
  'auth.login.emailPlaceholder': 'toi@grymwyrd.com',
  'auth.login.passwordLabel': 'Mot de passe',
  'auth.login.forgotPassword': 'Oublié ton mot de passe ?',
  'auth.login.submitBusy': 'Franchissement…',
  'auth.login.submit': 'Entrer',
  'auth.login.dividerOr': 'ou',
  'auth.login.magicSentPrefix': 'Un lien de connexion arrive',
  'auth.login.magicSentTo': 'à',
  'auth.login.magicSentSuffix': '. Vérifie ta boîte de réception.',
  'auth.login.emailMeSignInLink': 'Envoie-moi un lien de connexion par email',
  'auth.login.mockAuthPrefix': 'Auth de dev : tout email fonctionne. Utilise',
  'auth.login.mockAuthOr': 'ou',
  'auth.login.mockAuthSuffix': 'pour les rôles élevés.',

  // ── RegisterPage ─────────────────────────────────────────────────────────
  'auth.register.errorDisplayNameRequired': 'Choisis le nom sous lequel ta légende sera contée.',
  'auth.register.errorEmailRequired': 'Un email est requis pour sceller ton pacte.',
  'auth.register.errorEmailInvalid': 'Cet email ne semble pas correct.',
  'auth.register.errorPasswordTooShort': 'Ton mot secret doit comporter au moins 8 caractères.',
  'auth.register.errorMustBeAdult': 'Tu dois avoir au moins 13 ans pour rejoindre Grymwyrd.',
  'auth.register.errorMustAcceptTerms': 'Tu dois accepter le pacte (conditions) pour continuer.',
  'auth.register.errorIncomplete': 'Certaines marques de ton pacte sont incomplètes. Vois ci-dessous.',
  'auth.register.seoTitle': 'Commence Ton Récit',
  'auth.register.seoDescription':
    'Crée un compte Grymwyrd, élève un avatar, et fais que ton histoire devienne canon.',
  'auth.register.eyebrow': 'Explore le Village de Greywick',
  'auth.register.title': 'Commence Ton Récit',
  'auth.register.blurb': 'Tu t\'éveilles des rêves éthérés et ouvres les yeux…',
  'auth.register.alreadyBound': 'Déjà lié au crépuscule ?',
  'auth.register.loginToExisting': 'Connecte-toi à Ton Compte Existant',
  'auth.register.displayNameLabel': 'Nom d\'affichage',
  'auth.register.displayNamePlaceholder': 'Le Chevalier Creux',
  'auth.register.displayNameHint': 'Ceci nomme ton avatar public et tes contributions.',
  'auth.register.emailLabel': 'Email',
  'auth.register.emailPlaceholder': 'toi@grymwyrd.com',
  'auth.register.passwordLabel': 'Mot de passe',
  'auth.register.passwordPlaceholder': 'Au moins 8 caractères',
  'auth.register.agreementsLegend': 'Accords',
  'auth.register.adultConfirmPrefix': 'Je confirme que j\'ai',
  'auth.register.adultConfirmEmphasis': '13 ans ou plus',
  'auth.register.adultConfirmSuffix': '.',
  'auth.register.acceptPrefix': 'J\'accepte les',
  'auth.register.termsOfThePact': 'Conditions du Pacte',
  'auth.register.acceptSuffix': '.',
  'auth.register.submitBusy': 'Scellement du pacte…',
  'auth.register.submit': 'Émerger',
  'auth.register.dividerOr': 'ou',
  'auth.register.mockAuthNote': 'Auth de dev : l\'enregistrement persiste localement uniquement. Aucun email n\'est envoyé.',
  'auth.register.lastUpdated': 'Dernière mise à jour {date}',

  // ── ResetPasswordPage ────────────────────────────────────────────────────
  'auth.reset.errorInvalidEmail': 'Saisis l\'email lié à ton pacte.',
  'auth.reset.seoTitle': 'Réinitialiser Ton Mot de Passe',
  'auth.reset.seoDescription':
    'Oublié ton mot de passe ? Nous t\'enverrons un lien à usage unique ; clique dessus pour choisir un nouveau mot de passe.',
  'auth.reset.eyebrow': 'Le Mot Perdu',
  'auth.reset.title': 'Réinitialiser Ton Mot de Passe',
  'auth.reset.blurb':
    'Murmure ton email et un lien à usage unique te sera envoyé dans l\'obscurité. Suis-le, et tu définiras un nouveau mot de passe.',
  'auth.reset.rememberedAfterAll': 'Finalement tu t\'en es souvenu ?',
  'auth.reset.loginToExisting': 'Connecte-toi à Ton Compte Existant',
  'auth.reset.successMessage':
    'Si un compte existe pour {email}, un lien à usage unique t\'a été envoyé. Ouvre-le pour te connecter et définir ton nouveau mot de passe — vérifie ton email et la crypte des indésirables.',
  'auth.reset.sendToDifferentEmail': 'Envoyer à un autre email',
  'auth.reset.emailLabel': 'Email',
  'auth.reset.emailPlaceholder': 'toi@grymwyrd.com',
  'auth.reset.submitBusy': 'Invocation…',
  'auth.reset.submit': 'Envoyer un Lien de Connexion',

  // ── SetPasswordPage ──────────────────────────────────────────────────────
  'auth.setPassword.errorTooShort': 'Choisis un mot de passe d\'au moins 8 caractères.',
  'auth.setPassword.errorMismatch': 'Ces mots de passe ne correspondent pas.',
  'auth.setPassword.seoTitle': 'Définis Ton Mot de Passe',
  'auth.setPassword.seoDescription': 'Choisis un nouveau mot de passe pour ton compte Grymwyrd.',
  'auth.setPassword.eyebrow': 'Scelle le Pacte',
  'auth.setPassword.title': 'Définis un Nouveau Mot de Passe',
  'auth.setPassword.blurb':
    'Tu es connecté. Choisis un nouveau mot de passe — tu pourras l\'utiliser la prochaine fois aux côtés du lien magique.',
  'auth.setPassword.ratherNot': 'Tu préfères pas ?',
  'auth.setPassword.skipForNow': 'Passer pour l\'instant',
  'auth.setPassword.successMessage': 'Ton mot de passe est défini. Redirection vers ton sanctuaire…',
  'auth.setPassword.newPasswordLabel': 'Nouveau mot de passe',
  'auth.setPassword.newPasswordPlaceholder': 'Au moins 8 caractères',
  'auth.setPassword.confirmPasswordLabel': 'Confirmer le mot de passe',
  'auth.setPassword.confirmPasswordPlaceholder': 'Réessaie',
  'auth.setPassword.submitBusy': 'Scellement…',
  'auth.setPassword.submit': 'Enregistrer le Mot de Passe',
}
